/**
 * Contract C2 — Collected Material / IIR viewer navigation (published by P9;
 * consumed by P4, P5, P8, P10, P2).
 *
 * Opens the shared Sources file viewer modal for a Collected Material (or, later,
 * an IIR — P10) from any page by encoding viewer state in namespaced URL query
 * params on the caller's own URL. The globally mounted
 * {@link ../viewer/CollectionMaterialViewerHost} reacts to these params, so the
 * modal survives refresh and browser Back closes it naturally.
 *
 * URL-param format (namespaced `cmView*`, other params on the page are preserved):
 *   cmView=<materialId>
 *   cmViewKind=cm|iir            (defaults to 'cm' when absent)
 *   cmViewReturn=<relative URL>  (optional; exact prior UI state, e.g.
 *                                 "/icr/ICR-1?coverage=collection&tab=eei&eei=<id>")
 *
 * `returnTo` must be a relative URL (path + query + hash). When omitted it
 * defaults to the caller's current URL (minus any cmView* params), which is the
 * exact prior state. The modal's "<" back button navigates to it verbatim.
 */

import {sanitizeReturnTo} from '../collectionTaskPayload';

export const CM_VIEWER_PARAM_ID = 'cmView';
export const CM_VIEWER_PARAM_KIND = 'cmViewKind';
export const CM_VIEWER_PARAM_RETURN = 'cmViewReturn';

export const CM_MATERIAL_KIND_CM = 'cm';
export const CM_MATERIAL_KIND_IIR = 'iir';

const KNOWN_KINDS = new Set([CM_MATERIAL_KIND_CM, CM_MATERIAL_KIND_IIR]);

function toParams(search) {
    return new URLSearchParams((search || '').replace(/^\?/, ''));
}

function toSearchString(params) {
    const s = params.toString();
    return s ? `?${s}` : '';
}

/** Adds the cmView* params to an existing search string, preserving unrelated params. */
export function buildCollectionMaterialViewerSearch(currentSearch, {
    materialId,
    materialKind = CM_MATERIAL_KIND_CM,
    returnTo = null,
} = {}) {
    const params = toParams(currentSearch);
    params.set(CM_VIEWER_PARAM_ID, String(materialId));
    params.set(CM_VIEWER_PARAM_KIND, KNOWN_KINDS.has(materialKind) ? materialKind : CM_MATERIAL_KIND_CM);
    const safeReturnTo = sanitizeReturnTo(returnTo);
    if (safeReturnTo) {
        params.set(CM_VIEWER_PARAM_RETURN, safeReturnTo);
    } else {
        params.delete(CM_VIEWER_PARAM_RETURN);
    }
    return toSearchString(params);
}

/** Reads viewer state from a search string; null when the viewer is not open. */
export function readCollectionMaterialViewerState(search) {
    const params = toParams(search);
    const materialId = params.get(CM_VIEWER_PARAM_ID);
    if (!materialId) {
        return null;
    }
    const rawKind = params.get(CM_VIEWER_PARAM_KIND);
    return {
        materialId,
        materialKind: KNOWN_KINDS.has(rawKind) ? rawKind : CM_MATERIAL_KIND_CM,
        returnTo: sanitizeReturnTo(params.get(CM_VIEWER_PARAM_RETURN)),
    };
}

/** Removes only the cmView* params from a search string. */
export function stripCollectionMaterialViewerParams(search) {
    const params = toParams(search);
    params.delete(CM_VIEWER_PARAM_ID);
    params.delete(CM_VIEWER_PARAM_KIND);
    params.delete(CM_VIEWER_PARAM_RETURN);
    return toSearchString(params);
}

function currentLocationLike() {
    if (typeof window === 'undefined') {
        return {pathname: '/', search: '', hash: ''};
    }
    return window.location;
}

/**
 * Opens the material viewer (contract C2).
 *
 * @param {Object} args
 * @param {string} args.materialId  Collected Material id (or IIR id for kind 'iir').
 * @param {'cm'|'iir'} [args.materialKind='cm']
 * @param {string} [args.returnTo]  Relative URL of the exact prior UI state; defaults
 *                                  to the current URL (without cmView* params).
 * @param {Function} [args.navigate]  react-router navigate; preferred (see
 *                                    useCollectionMaterialViewer). Falls back to a
 *                                    history push + popstate dispatch when absent.
 * @param {Object} [args.location]  react-router location; defaults to window.location.
 */
export default function openCollectionMaterial({
    materialId,
    materialKind = CM_MATERIAL_KIND_CM,
    returnTo,
    navigate,
    location,
} = {}) {
    if (!materialId) {
        console.warn('[openCollectionMaterial] called without materialId');
        return;
    }

    const loc = location || currentLocationLike();
    const baseSearch = stripCollectionMaterialViewerParams(loc.search || '');
    const effectiveReturnTo = sanitizeReturnTo(returnTo)
        || `${loc.pathname}${baseSearch}${loc.hash || ''}`;

    const search = buildCollectionMaterialViewerSearch(loc.search || '', {
        materialId,
        materialKind,
        returnTo: effectiveReturnTo,
    });
    const to = `${loc.pathname}${search}${loc.hash || ''}`;

    if (navigate) {
        navigate(to);
        return;
    }

    // Non-hook contexts: push the URL and nudge react-router to re-read it.
    window.history.pushState({}, '', to);
    window.dispatchEvent(new PopStateEvent('popstate'));
}
